/* ============================================================
   Mard Hazraat — Cart Page (mh-cart.js)
   ------------------------------------------------------------
   - AJAX quantity updates, remove, discount apply/remove
   - Auto-saving order notes (debounced)
   - Delivery date estimate (business-day aware)
   - Toast notifications
   - Optimistic UI with rollback on error
   - Guards against Dawn JS interference
   ============================================================ */

(function () {
  'use strict';

  const root = document.querySelector('[data-mh-cart]');
  if (!root) return;

  // Internal flag — true while we're programmatically updating the input
  // Prevents the input/change listeners from re-firing API calls
  let isProgrammaticUpdate = false;

  // ---------- Money formatting ----------
  // Prefer the shop's real format passed from Liquid (data attributes), then
  // window.Shopify, then a PKR-safe default. The old '${{amount}}' fallback
  // caused updated prices to render in USD ($) when window.Shopify wasn't
  // populated, while the initial Liquid-rendered prices stayed in PKR.
  const MONEY_FORMAT =
    root.dataset.moneyFormat ||
    (window.Shopify && window.Shopify.money_format) ||
    'Rs.{{amount}}';

  const MONEY_WITH_CURRENCY_FORMAT =
    root.dataset.moneyWithCurrencyFormat ||
    (window.Shopify && window.Shopify.money_with_currency_format) ||
    MONEY_FORMAT;

  function formatMoney(cents) {
    cents = Number(cents) || 0;
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = parts[1] ? intPart + '.' + parts[1] : intPart;
    return MONEY_FORMAT
      .replace(/\{\{\s*amount\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, intPart)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, intPart);
  }

  function formatMoneyWithCurrency(cents) {
    const moneyFormatWithCurrency = MONEY_WITH_CURRENCY_FORMAT;
    cents = Number(cents) || 0;
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = parts[1] ? intPart + '.' + parts[1] : intPart;
    return moneyFormatWithCurrency
      .replace(/\{\{\s*amount\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, intPart)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, intPart);
  }

  // ---------- Utilities ----------
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || root).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    let t;
    return function () {
      const ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // Set input value WITHOUT triggering input/change events
  function setInputValueSilently(input, value) {
    isProgrammaticUpdate = true;
    input.value = value;
    // Reset flag on next tick so any synchronous events fire while flag is true
    setTimeout(function () { isProgrammaticUpdate = false; }, 0);
  }

  // ============================================================
  //  DELIVERY DATE ESTIMATE
  // ============================================================
  function computeDeliveryDates() {
    const minDays = parseInt(root.dataset.deliveryMin, 10) || 3;
    const maxDays = parseInt(root.dataset.deliveryMax, 10) || 5;
    const skipSundays = root.dataset.skipSundays === 'true';
    const skipSaturdays = root.dataset.skipSaturdays === 'true';

    function addBusinessDays(start, days) {
      const date = new Date(start);
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (skipSundays && day === 0) continue;
        if (skipSaturdays && day === 6) continue;
        added++;
      }
      return date;
    }

    function formatDate(d) {
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }

    const today = new Date();
    const from = addBusinessDays(today, minDays);
    const to = addBusinessDays(today, maxDays);

    const fromEl = $('[data-delivery-from]');
    const toEl = $('[data-delivery-to]');
    if (fromEl) fromEl.textContent = formatDate(from);
    if (toEl) toEl.textContent = formatDate(to);
  }

  // ============================================================
  //  TOAST
  // ============================================================
  const toastContainer = $('[data-toast-container]');
  const TOAST_DURATION = 3500;

  const ICON_CHECK = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 10l4 4 8-8"/></svg>';
  const ICON_X = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15"/></svg>';

  function showToast(message, type) {
    if (!toastContainer) return;
    type = type || 'success';

    const toast = document.createElement('div');
    toast.className = 'mh-cart__toast mh-cart__toast--' + type;
    toast.innerHTML =
      (type === 'success' ? ICON_CHECK : ICON_X) +
      '<span>' + escapeHTML(message) + '</span>';

    toastContainer.appendChild(toast);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });
    });

    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }, TOAST_DURATION);
  }

  function escapeHTML(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ============================================================
  //  CART API HELPERS
  // ============================================================
  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function changeCart(payload) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (err) { throw err; });
      }
      return r.json();
    });
  }

  function updateCart(payload) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (err) { throw err; });
      }
      return r.json();
    });
  }

  // ============================================================
  //  RENDER HELPERS
  // ============================================================
  function renderLineItemPrices(itemEl, item) {
    const priceEl = $('[data-line-price]', itemEl);
    if (priceEl) {
      priceEl.innerHTML = item.original_price > item.final_price
        ? '<span class="mh-cart__price-compare">' + formatMoney(item.original_price) + '</span>' +
          '<span class="mh-cart__price-final mh-cart__price-final--sale">' + formatMoney(item.final_price) + '</span>'
        : '<span class="mh-cart__price-final">' + formatMoney(item.final_price) + '</span>';
    }

    const totalEl = $('[data-line-total]', itemEl);
    if (totalEl) {
      totalEl.innerHTML = item.original_line_price > item.final_line_price
        ? '<span class="mh-cart__price-compare">' + formatMoney(item.original_line_price) + '</span>' +
          '<span class="mh-cart__price-final mh-cart__price-final--sale">' + formatMoney(item.final_line_price) + '</span>'
        : '<span class="mh-cart__price-final">' + formatMoney(item.final_line_price) + '</span>';
    }
  }

  function renderTotals(cart) {
    const subtotalEl = $('[data-cart-subtotal]');
    const savingsRow = $('[data-cart-savings-row]');
    const savingsEl = $('[data-cart-savings]');
    const totalEl = $('[data-cart-total]');

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);

    if (savingsRow && savingsEl) {
      if (cart.total_discount > 0) {
        savingsEl.textContent = '−' + formatMoney(cart.total_discount);
        savingsRow.hidden = false;
      } else {
        savingsRow.hidden = true;
      }
    }

    // Grand total: items subtotal minus any cart-level discount. We do NOT use
    // cart.total_price here because a cart-level fee (e.g. COD/shipping app)
    // can inflate it; shipping is shown as "calculated at checkout" instead.
    if (totalEl) {
      var grandTotal = (cart.items_subtotal_price || 0) - (cart.total_discount || 0);
      totalEl.textContent = formatMoneyWithCurrency(grandTotal);
    }
  }

  function renderAppliedDiscounts(cart) {
    const container = $('[data-applied-discounts]');
    if (!container) return;

    container.innerHTML = '';
    const apps = cart.cart_level_discount_applications || [];
    apps.forEach(function (d) {
      const title = d.title || d.code || '';
      if (!title) return;
      const chip = document.createElement('span');
      chip.className = 'mh-cart__discount-chip';
      chip.setAttribute('data-discount-chip', '');
      chip.innerHTML =
        '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
        '<path d="M2 7l5 5 5-5-5-5z"/>' +
        '<circle cx="7" cy="7" r="1" fill="currentColor"/>' +
        '</svg>' +
        '<span>' + escapeHTML(title) + '</span>' +
        '<button type="button" class="mh-cart__discount-remove" data-discount-remove="' + escapeHTML(title) + '" aria-label="Remove discount ' + escapeHTML(title) + '">' +
        '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
        '<path d="M3 3l6 6M9 3l-6 6"/>' +
        '</svg>' +
        '</button>';
      container.appendChild(chip);
    });
  }

  function syncCart(cart) {
    // Shopify regenerates the line item key hash (the part after ":") on every
    // cart change when an item has properties/selling plans. The variant ID
    // (part before ":") stays stable. We match by variant ID and update keys
    // after sync to keep DOM in step with the latest cart state.
    function keyPrefix(k) {
      if (!k) return '';
      const idx = k.indexOf(':');
      return idx === -1 ? k : k.substring(0, idx);
    }

    const itemsByKey = {};
    const itemsByPrefix = {};
    (cart.items || []).forEach(function (it) {
      itemsByKey[it.key] = it;
      // If multiple lines share the same variant ID (different properties),
      // we can't disambiguate by prefix alone — fall back to full key match
      // and only use prefix when unique
      const p = keyPrefix(it.key);
      if (itemsByPrefix[p]) {
        itemsByPrefix[p] = '__ambiguous__';
      } else {
        itemsByPrefix[p] = it;
      }
    });

    $$('[data-cart-item]').forEach(function (itemEl) {
      const domKey = itemEl.dataset.key;
      let item = itemsByKey[domKey];

      // Fallback: if exact key not found, try matching by variant ID prefix
      if (!item) {
        const candidate = itemsByPrefix[keyPrefix(domKey)];
        if (candidate && candidate !== '__ambiguous__') {
          item = candidate;
          // Update DOM key to match the new server-generated key
          itemEl.dataset.key = item.key;
        }
      }

      if (!item) {
        // Truly removed — animate out
        itemEl.classList.add('is-removing');
        setTimeout(function () {
          if (itemEl.parentNode) itemEl.parentNode.removeChild(itemEl);
          if (cart.item_count === 0) {
            window.location.reload();
          }
        }, 320);
        return;
      }
      const qtyInput = $('[data-qty-input]', itemEl);
      if (qtyInput && parseInt(qtyInput.value, 10) !== item.quantity) {
        setInputValueSilently(qtyInput, item.quantity);
      }
      renderLineItemPrices(itemEl, item);
    });

    renderTotals(cart);
    renderAppliedDiscounts(cart);
    root.dataset.cartItemCount = String(cart.item_count);

    document.dispatchEvent(new CustomEvent('mh:cart:updated', { detail: cart }));
  }

  // ============================================================
  //  QUANTITY HANDLERS
  // ============================================================
  function setItemLoading(itemEl, isLoading) {
    if (isLoading) {
      itemEl.classList.add('is-updating');
    } else {
      itemEl.classList.remove('is-updating');
    }
  }

  // Track in-flight request per item to prevent race conditions
  const inFlight = {};

  function changeItemQuantity(itemEl, newQty) {
    const key = itemEl.dataset.key;
    if (!key) return Promise.reject(new Error('No item key'));

    // Use variant ID prefix as stable identifier (Shopify regenerates the hash
    // suffix on every cart change for items with properties/selling plans)
    const prefix = key.indexOf(':') === -1 ? key : key.substring(0, key.indexOf(':'));

    const qtyInput = $('[data-qty-input]', itemEl);
    const oldQty = qtyInput ? parseInt(qtyInput.value, 10) : 0;

    newQty = Math.max(0, parseInt(newQty, 10) || 0);

    // Skip if already at this quantity and not removing
    if (newQty === oldQty && newQty !== 0) {
      return Promise.resolve();
    }

    // Cancel logic: if there's an in-flight request for this item, just queue
    // the new value — we'll send the latest desired qty when the current one returns
    if (inFlight[prefix]) {
      inFlight[prefix].pendingQty = newQty;
      if (qtyInput) setInputValueSilently(qtyInput, newQty);
      return inFlight[prefix].promise;
    }

    // Optimistic update
    if (qtyInput) setInputValueSilently(qtyInput, newQty);
    setItemLoading(itemEl, true);

    const request = {
      pendingQty: null,
      promise: null
    };

    request.promise = changeCart({ id: key, quantity: newQty })
      .then(function (cart) {
        syncCart(cart);
        return cart;
      })
      .catch(function (err) {
        if (qtyInput) setInputValueSilently(qtyInput, oldQty);
        const msg = (err && err.description) || (err && err.message) || 'Could not update item';
        showToast(msg, 'error');
        throw err;
      })
      .finally(function () {
        setItemLoading(itemEl, false);
        const pending = inFlight[prefix] && inFlight[prefix].pendingQty;
        delete inFlight[prefix];
        // If user changed qty during the request, fire the latest desired qty
        if (pending !== null && pending !== undefined && pending !== newQty) {
          changeItemQuantity(itemEl, pending);
        }
      });

    inFlight[prefix] = request;
    return request.promise;
  }

  const debouncedQtyChange = debounce(function (itemEl, newQty) {
    changeItemQuantity(itemEl, newQty);
  }, 400);

  // ============================================================
  //  EVENT DELEGATION
  // ============================================================
  root.addEventListener('click', function (e) {
    // Qty decrease
    const decBtn = e.target.closest('[data-qty-decrease]');
    if (decBtn) {
      e.preventDefault();
      e.stopPropagation();
      const itemEl = decBtn.closest('[data-cart-item]');
      if (!itemEl) return;
      const qtyInput = $('[data-qty-input]', itemEl);
      if (!qtyInput) return;
      const current = parseInt(qtyInput.value, 10) || 0;
      const next = Math.max(0, current - 1);
      changeItemQuantity(itemEl, next);
      return;
    }

    // Qty increase
    const incBtn = e.target.closest('[data-qty-increase]');
    if (incBtn) {
      e.preventDefault();
      e.stopPropagation();
      const itemEl = incBtn.closest('[data-cart-item]');
      if (!itemEl) return;
      const qtyInput = $('[data-qty-input]', itemEl);
      if (!qtyInput) return;
      const current = parseInt(qtyInput.value, 10) || 0;
      changeItemQuantity(itemEl, current + 1);
      return;
    }

    // Remove item
    const removeBtn = e.target.closest('[data-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const itemEl = removeBtn.closest('[data-cart-item]');
      if (!itemEl) return;
      changeItemQuantity(itemEl, 0);
      return;
    }

    // Apply discount
    const applyBtn = e.target.closest('[data-discount-apply]');
    if (applyBtn) {
      e.preventDefault();
      e.stopPropagation();
      applyDiscount();
      return;
    }

    // Remove discount
    const discRemove = e.target.closest('[data-discount-remove]');
    if (discRemove) {
      e.preventDefault();
      e.stopPropagation();
      removeDiscount(discRemove.getAttribute('data-discount-remove'));
      return;
    }
  }, true); // Use capture phase so we run BEFORE any Dawn listeners

  // Qty input direct edit (blur/change)
  root.addEventListener('change', function (e) {
    if (isProgrammaticUpdate) return;
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    e.stopPropagation();
    const itemEl = input.closest('[data-cart-item]');
    if (!itemEl) return;
    const val = Math.max(0, parseInt(input.value, 10) || 0);
    changeItemQuantity(itemEl, val);
  });

  // Qty input live keystroke (debounced)
  root.addEventListener('input', function (e) {
    if (isProgrammaticUpdate) return;
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    e.stopPropagation();
    const itemEl = input.closest('[data-cart-item]');
    if (!itemEl) return;
    const val = Math.max(0, parseInt(input.value, 10) || 0);
    debouncedQtyChange(itemEl, val);
  });

  // Enter key in discount input
  const discInput = $('[data-discount-input]');
  if (discInput) {
    discInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        applyDiscount();
      }
    });
  }

  // ============================================================
  //  DISCOUNT CODE
  // ============================================================
  function applyDiscount() {
    const input = $('[data-discount-input]');
    const btn = $('[data-discount-apply]');
    if (!input || !btn) return;

    const code = (input.value || '').trim();
    if (!code) {
      showToast('Please enter a discount code', 'error');
      input.focus();
      return;
    }

    btn.classList.add('is-loading');
    btn.disabled = true;

    fetchCart()
      .then(function (cart) {
        const existing = (cart.cart_level_discount_applications || [])
          .map(function (d) { return d.title || d.code; })
          .filter(Boolean);

        const codeUpper = code.toUpperCase();
        const alreadyApplied = existing.some(function (c) { return c.toUpperCase() === codeUpper; });
        if (alreadyApplied) {
          showToast('Discount "' + code + '" is already applied', 'error');
          btn.classList.remove('is-loading');
          btn.disabled = false;
          return;
        }

        const combined = existing.concat([code]).join(',');
        return updateCart({ discount: combined }).then(function (updatedCart) {
          const appliedNow = (updatedCart.cart_level_discount_applications || [])
            .map(function (d) { return (d.title || d.code || '').toUpperCase(); });
          const accepted = appliedNow.indexOf(codeUpper) !== -1;

          if (accepted) {
            input.value = '';
            showToast('Discount applied', 'success');
          } else {
            showToast('Discount code not valid', 'error');
          }
          syncCart(updatedCart);
        });
      })
      .catch(function (err) {
        const msg = (err && err.description) || (err && err.message) || 'Could not apply discount';
        showToast(msg, 'error');
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  }

  function removeDiscount(codeToRemove) {
    if (!codeToRemove) return;

    fetchCart()
      .then(function (cart) {
        const remaining = (cart.cart_level_discount_applications || [])
          .map(function (d) { return d.title || d.code; })
          .filter(function (c) { return c && c.toUpperCase() !== codeToRemove.toUpperCase(); });

        return updateCart({ discount: remaining.join(',') });
      })
      .then(function (updatedCart) {
        showToast('Discount removed', 'success');
        syncCart(updatedCart);
      })
      .catch(function (err) {
        const msg = (err && err.description) || (err && err.message) || 'Could not remove discount';
        showToast(msg, 'error');
      });
  }

  // ============================================================
  //  ORDER NOTES (debounced auto-save)
  // ============================================================
  const noteEl = $('[data-cart-note]');
  if (noteEl) {
    const saveNote = debounce(function (value) {
      updateCart({ note: value })
        .catch(function (err) {
          const msg = (err && err.description) || (err && err.message) || 'Could not save note';
          showToast(msg, 'error');
        });
    }, 700);

    noteEl.addEventListener('input', function (e) {
      e.stopPropagation();
      saveNote(noteEl.value);
    });
  }

  // ============================================================
  //  CHECKOUT BUTTON LOADING STATE
  // ============================================================
  const checkoutBtn = $('[data-checkout]');
  const cartForm = $('#cart');
  if (cartForm && checkoutBtn) {
    cartForm.addEventListener('submit', function () {
      // Show the visual loading state only. Do NOT disable the button here:
      // disabling a submit button inside its own submit handler cancels the
      // form submission in most browsers, and also drops the button's
      // name="checkout" from the POST data that tells Shopify to go to
      // checkout. The result is a spinner that never navigates.
      checkoutBtn.classList.add('is-loading');
      // Guard against rapid double-clicks WITHOUT blocking this submission:
      // disable on a later tick, after the browser has already begun the
      // navigation, and re-enable if the user comes back (bfcache).
      setTimeout(function () {
        checkoutBtn.disabled = true;
      }, 0);
    });

    // If the user navigates back to the cart (browser back / bfcache restore),
    // restore the button so it works again.
    window.addEventListener('pageshow', function () {
      checkoutBtn.classList.remove('is-loading');
      checkoutBtn.disabled = false;
    });
  }

  // ============================================================
  //  EXTERNAL CART UPDATES (e.g., from drawer)
  // ============================================================
  document.addEventListener('mh:cart:external-update', function () {
    fetchCart().then(syncCart).catch(function () {});
  });

  // ============================================================
  //  INITIALIZE
  // ============================================================
  computeDeliveryDates();
})();